import { type ChangeEvent, useRef } from 'react';
import { useCameraConfig } from '@/camera/cameraConfig';
import { gridColumns } from '@/camera/gridColumns';
import { useBridges } from '@/camera/useBridges';
import { samePhotos } from '@/hooks/selectEqual';
import { useWakeLock } from '@/hooks/useWakeLock';
import { useRowboat, useSelect } from '@/rowboat';
import { photosForItem } from '@/services/photoService';
import { BridgeCameraTile, LocalCameraTile } from './camera/CameraTile';
import { PhotoGrid } from './PhotoGrid';
import { useModal } from './ui';

/** Copies the picked files and clears the input so the same file can be picked again. */
export function takeFiles(e: ChangeEvent<HTMLInputElement>): File[] {
  // Copy first: clearing the value empties the live FileList.
  const files = Array.from(e.target.files ?? []);
  e.target.value = '';
  return files;
}

/** Shots and gallery picks keep the camera open, so one visit can photograph an item fully. */
export function CameraView({
  itemId,
  previews,
  saved = [],
  error: captureError = '',
  onFiles,
  onClose,
}: {
  itemId: string;
  previews: Record<string, string>;
  /** Ids of the photos this item's captures saved, oldest first. */
  saved?: string[];
  /** A failed save of a shot or pick; the inspector behind the camera cannot show it. */
  error?: string;
  onFiles: (files: File[]) => void;
  onClose: () => void;
}) {
  const closeRef = useModal(onClose);
  useWakeLock(true);
  const graph = useRowboat();
  const photos = useSelect(() => photosForItem(graph, itemId), samePhotos);
  const savedAtOpen = useRef(saved.length);
  // Counting this visit's own saves, not rows, leaves out photos synced in from another
  // device; checking them against the item drops any deleted in the viewer since.
  const added = saved
    .slice(savedAtOpen.current)
    .filter((id) => photos.some((p) => p.id === id)).length;
  const [config] = useCameraConfig();
  const bridges = useBridges({ poll: true }).filter((b) => b.active && !b.hidden);
  const count = bridges.length + (config.local.active ? 1 : 0);
  const columns = gridColumns(count);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Camera"
      className="fixed inset-0 z-40 flex flex-col bg-black text-white"
    >
      <div className="flex items-center justify-between gap-2 px-2 pt-[max(0.25rem,env(safe-area-inset-top))]">
        <button
          ref={closeRef}
          type="button"
          aria-label="Close camera"
          onClick={onClose}
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl text-xl hover:bg-white/10"
        >
          <span aria-hidden="true">✕</span>
        </button>
        <p aria-live="polite" className="ml-auto pr-3 text-sm">
          {added > 0 && `${added} photo${added === 1 ? '' : 's'} added`}
        </p>
        <label
          title="Choose from gallery"
          className="relative inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-xl border border-white/60 hover:bg-white/10"
        >
          <GalleryIcon />
          <input
            type="file"
            accept="image/*"
            multiple
            aria-label="Choose from gallery"
            onChange={(e) => {
              const files = takeFiles(e);
              if (files.length > 0) onFiles(files);
            }}
            className="absolute inset-0 cursor-pointer opacity-0"
          />
        </label>
      </div>
      {captureError && (
        <p role="alert" className="px-3 pb-1 text-center text-sm">
          {captureError}
        </p>
      )}
      {count === 0 ? (
        <p className="flex min-h-0 flex-1 basis-0 items-center justify-center px-6 text-center">
          No cameras are on. Turn one on in Menu → Cameras.
        </p>
      ) : (
        <div
          className="grid min-h-0 flex-1 basis-0 gap-1 overflow-y-auto"
          style={{
            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            // Up to six tiles fill the half; more scroll in rows of half its height.
            gridAutoRows: count > 6 ? '50%' : 'minmax(0, 1fr)',
          }}
        >
          {config.local.active && (
            <LocalCameraTile
              deviceId={config.local.deviceId}
              zoom={config.local.zoom}
              onFiles={onFiles}
            />
          )}
          {bridges.map((b) => (
            <BridgeCameraTile key={b.id} bridgeId={b.id} name={b.name} onFiles={onFiles} />
          ))}
        </div>
      )}
      <div className="min-h-0 flex-1 basis-0 overflow-y-auto border-t border-white/20 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        <PhotoGrid itemId={itemId} previews={previews} label="Item photos" highlightNew />
      </div>
    </div>
  );
}

function GalleryIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" />
    </svg>
  );
}
